/**
 * DataPrepPanel Component
 * 
 * Main container for the Data Prep feature. Shows:
 * - Dataset preview with transformed data
 * - List of transformation steps
 * - Add step dropdown
 * - Drag-and-drop reordering
 * - Undo functionality
 * - Export button
 */

import React, { useState, useMemo, useCallback, useRef } from 'react';
import {
    Plus, ChevronDown, Undo2, Download, Loader2, RefreshCw, Wand2,
    AlertCircle, Copy, Type, RefreshCw as RefreshIcon, TrendingUp, Calculator, Columns
} from 'lucide-react';
import type { Dataset, Column } from '../../types';
import type {
    DataPrepStepWithMeta,
    DataPrepStep,
    DataPrepStepType,
    ColumnSchema,
    DataPrepPipelineResult,
    StepResult
} from '../../services/dataPrepTypes';
import { applyDataPrepPipeline, applyFullTransform, invalidateCache, computeSchema } from '../../services/dataPrepEngine';
import { DataPrepStepCard } from './DataPrepStepCard';
import { DataPrepPreview } from './DataPrepPreview';
import { DataPrepModal } from './DataPrepModals';

// ============================================================================
// Types
// ============================================================================

interface DataPrepPanelProps {
    dataset: Dataset;
    onStepsChange: (steps: DataPrepStepWithMeta[]) => void;
}

interface UndoState {
    steps: DataPrepStepWithMeta[];
    description: string;
}

// ============================================================================
// Helper Functions
// ============================================================================

function generateId(): string {
    return `step_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

const stepTypeOptions: { type: DataPrepStepType; label: string; icon: React.FC<{ className?: string }> }[] = [
    { type: 'null-handling', label: 'Handle Null Values', icon: AlertCircle },
    { type: 'duplicate-handling', label: 'Remove Duplicates', icon: Copy },
    { type: 'string-cleaning', label: 'Clean Strings', icon: Type },
    { type: 'type-casting', label: 'Convert Types', icon: RefreshIcon },
    { type: 'normalization', label: 'Normalize', icon: TrendingUp },
    { type: 'calculated-column', label: 'Add Calculated Column', icon: Calculator },
    { type: 'column-management', label: 'Manage Columns', icon: Columns }
];

// ============================================================================
// Component
// ============================================================================

export const DataPrepPanel: React.FC<DataPrepPanelProps> = ({ dataset, onStepsChange }) => {
    // State
    const [steps, setSteps] = useState<DataPrepStepWithMeta[]>(dataset.prepSteps || []);
    const [undoStack, setUndoStack] = useState<UndoState[]>([]);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [editingStep, setEditingStep] = useState<{ id: string | null; type: DataPrepStepType } | null>(null);
    const [isExporting, setIsExporting] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Memoized pipeline result
    const pipelineResult = useMemo<DataPrepPipelineResult>(() => {
        const rawData = dataset.sampleRows || dataset.data || [];
        const baseSchema = dataset.schema.map(c => ({ name: c.name, type: c.type }));
        return applyDataPrepPipeline(dataset.id, rawData, baseSchema, steps);
    }, [dataset.id, dataset.sampleRows, dataset.data, dataset.schema, steps]);

    // Get current schema for modals
    const currentSchema = pipelineResult.schema;

    // ============================================================================
    // Step Management
    // ============================================================================

    const pushUndo = useCallback((description: string, currentSteps: DataPrepStepWithMeta[]) => {
        setUndoStack(prev => [...prev.slice(-19), { steps: currentSteps, description }]);
    }, []);

    const updateSteps = useCallback((newSteps: DataPrepStepWithMeta[], description: string) => {
        pushUndo(description, steps);
        setSteps(newSteps);
        onStepsChange(newSteps);
        invalidateCache(dataset.id);
    }, [steps, onStepsChange, dataset.id, pushUndo]);

    const handleAddStep = useCallback((type: DataPrepStepType) => {
        setIsDropdownOpen(false);
        setEditingStep({ id: null, type });
    }, []);

    const handleSaveStep = useCallback((step: DataPrepStep) => {
        if (!editingStep) return;

        if (editingStep.id) {
            // Update existing step
            const newSteps = steps.map(s =>
                s.id === editingStep.id ? { ...s, step } : s
            );
            updateSteps(newSteps, `Updated ${step.type} step`);
        } else {
            // Add new step
            const newStep: DataPrepStepWithMeta = {
                id: generateId(),
                step,
                enabled: true,
                order: steps.length
            };
            updateSteps([...steps, newStep], `Added ${step.type} step`);
        }

        setEditingStep(null);
    }, [editingStep, steps, updateSteps]);

    const handleToggleStep = useCallback((id: string, enabled: boolean) => {
        const newSteps = steps.map(s =>
            s.id === id ? { ...s, enabled } : s
        );
        updateSteps(newSteps, enabled ? 'Enabled step' : 'Disabled step');
    }, [steps, updateSteps]);

    const handleEditStep = useCallback((id: string) => {
        const stepMeta = steps.find(s => s.id === id);
        if (stepMeta) {
            setEditingStep({ id, type: stepMeta.step.type });
        }
    }, [steps]);

    const handleDeleteStep = useCallback((id: string) => {
        const newSteps = steps.filter(s => s.id !== id);
        // Recompute order
        newSteps.forEach((s, idx) => s.order = idx);
        updateSteps(newSteps, 'Deleted step');
    }, [steps, updateSteps]);

    const handleUndo = useCallback(() => {
        if (undoStack.length === 0) return;
        const lastState = undoStack[undoStack.length - 1];
        setUndoStack(prev => prev.slice(0, -1));
        setSteps(lastState.steps);
        onStepsChange(lastState.steps);
        invalidateCache(dataset.id);
    }, [undoStack, onStepsChange, dataset.id]);

    // ============================================================================
    // Export
    // ============================================================================

    const handleExport = useCallback(async () => {
        setIsExporting(true);
        try {
            const rawData = dataset.sampleRows || dataset.data || [];
            const baseSchema = dataset.schema.map(c => ({ name: c.name, type: c.type }));
            const fullResult = applyFullTransform(rawData, baseSchema, steps);

            // Build CSV
            const visibleSchema = fullResult.schema.filter(c => !c.hidden);
            const headers = visibleSchema.map(c => c.name);
            const rows = fullResult.data.map(row =>
                headers.map(h => {
                    const val = row[h];
                    if (val === null || val === undefined) return '';
                    // Escape quotes and wrap in quotes if contains comma
                    const str = String(val);
                    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
                        return `"${str.replace(/"/g, '""')}"`;
                    }
                    return str;
                }).join(',')
            );

            const csv = [headers.join(','), ...rows].join('\n');
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = url;
            link.download = `${dataset.name}_transformed.csv`;
            link.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            console.error('Export failed:', e);
        }
        setIsExporting(false);
    }, [dataset, steps]);

    // ============================================================================
    // Drag and Drop (simplified - will be enhanced with @dnd-kit)
    // ============================================================================

    const [draggedId, setDraggedId] = useState<string | null>(null);

    const handleDragStart = useCallback((id: string) => {
        setDraggedId(id);
    }, []);

    const handleDragOver = useCallback((e: React.DragEvent, targetId: string) => {
        e.preventDefault();
        if (!draggedId || draggedId === targetId) return;

        const fromIdx = steps.findIndex(s => s.id === draggedId);
        const toIdx = steps.findIndex(s => s.id === targetId);
        if (fromIdx === -1 || toIdx === -1) return;

        const newSteps = [...steps];
        const [removed] = newSteps.splice(fromIdx, 1);
        newSteps.splice(toIdx, 0, removed);
        newSteps.forEach((s, idx) => s.order = idx);
        setSteps(newSteps);
    }, [draggedId, steps]);

    const handleDragEnd = useCallback(() => {
        if (draggedId) {
            onStepsChange(steps);
            invalidateCache(dataset.id);
            pushUndo('Reordered steps', steps);
        }
        setDraggedId(null);
    }, [draggedId, steps, onStepsChange, dataset.id, pushUndo]);

    // ============================================================================
    // Render
    // ============================================================================

    const sortedSteps = useMemo(() =>
        [...steps].sort((a, b) => a.order - b.order),
        [steps]
    );

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-neon-gold/10 rounded-lg">
                        <Wand2 className="w-5 h-5 text-neon-gold" />
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-gray-200">Data Prep</h3>
                        <p className="text-sm text-gray-500">
                            {steps.length} step{steps.length !== 1 ? 's' : ''} • {pipelineResult.data.length.toLocaleString()} rows
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    {/* Undo Button */}
                    <button
                        onClick={handleUndo}
                        disabled={undoStack.length === 0}
                        className={`p-2 rounded-lg transition-colors ${undoStack.length > 0
                                ? 'text-gray-400 hover:text-gray-200 hover:bg-white/5'
                                : 'text-gray-600 cursor-not-allowed'
                            }`}
                        title={undoStack.length > 0 ? `Undo: ${undoStack[undoStack.length - 1].description}` : 'Nothing to undo'}
                    >
                        <Undo2 className="w-4 h-4" />
                    </button>

                    {/* Export Button */}
                    <button
                        onClick={handleExport}
                        disabled={isExporting || pipelineResult.data.length === 0}
                        className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-400 hover:text-gray-200 hover:bg-white/5 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isExporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                        Export CSV
                    </button>

                    {/* Add Step Dropdown */}
                    <div ref={dropdownRef} className="relative">
                        <button
                            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                            className="flex items-center gap-2 px-4 py-2 bg-neon-gold text-gray-900 font-medium text-sm rounded-lg hover:bg-neon-gold/90 transition-colors"
                        >
                            <Plus className="w-4 h-4" />
                            Add Step
                            <ChevronDown className={`w-4 h-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                        </button>

                        {isDropdownOpen && (
                            <div className="absolute right-0 top-full mt-2 w-56 bg-gray-900 border border-white/10 rounded-lg shadow-xl z-20 py-1">
                                {stepTypeOptions.map(({ type, label, icon: Icon }) => (
                                    <button
                                        key={type}
                                        onClick={() => handleAddStep(type)}
                                        className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm text-gray-300 hover:bg-white/5 hover:text-gray-100 transition-colors"
                                    >
                                        <Icon className="w-4 h-4 text-gray-500" />
                                        {label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Steps List */}
            {sortedSteps.length > 0 ? (
                <div className="space-y-2">
                    {sortedSteps.map(stepMeta => (
                        <div
                            key={stepMeta.id}
                            draggable
                            onDragStart={() => handleDragStart(stepMeta.id)}
                            onDragOver={(e) => handleDragOver(e, stepMeta.id)}
                            onDragEnd={handleDragEnd}
                        >
                            <DataPrepStepCard
                                stepWithMeta={stepMeta}
                                stepResult={pipelineResult.stepResults.get(stepMeta.id)}
                                onToggle={handleToggleStep}
                                onEdit={handleEditStep}
                                onDelete={handleDeleteStep}
                                isDragging={draggedId === stepMeta.id}
                            />
                        </div>
                    ))}
                </div>
            ) : (
                <div className="text-center py-8 border-2 border-dashed border-white/10 rounded-xl">
                    <Wand2 className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                    <p className="text-gray-500 text-sm">No transformation steps yet</p>
                    <p className="text-gray-600 text-xs mt-1">Click "Add Step" to start cleaning your data</p>
                </div>
            )}

            {/* Warnings Summary */}
            {pipelineResult.warnings.length > 0 && (
                <div className="flex items-start gap-2 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                    <AlertCircle className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-yellow-400">
                        {pipelineResult.warnings.length} warning{pipelineResult.warnings.length !== 1 ? 's' : ''} during transformation
                    </div>
                </div>
            )}

            {/* Preview */}
            <div className="border border-white/10 rounded-xl overflow-hidden">
                <div className="px-4 py-3 bg-white/5 border-b border-white/10 flex items-center justify-between">
                    <h4 className="text-sm font-medium text-gray-300">Preview</h4>
                    <span className="text-xs text-gray-500">
                        {pipelineResult.stats.totalExecutionTimeMs.toFixed(0)}ms
                        {pipelineResult.stats.wasCached && ' (cached)'}
                    </span>
                </div>
                <DataPrepPreview
                    data={pipelineResult.data}
                    schema={pipelineResult.schema}
                    maxRows={50}
                    isPreviewMode={pipelineResult.stats.isPreviewMode}
                    totalRows={pipelineResult.stats.totalInputRows}
                />
            </div>

            {/* Edit Modal */}
            {editingStep && (
                <DataPrepModal
                    stepType={editingStep.type}
                    columns={currentSchema}
                    initialStep={editingStep.id ? steps.find(s => s.id === editingStep.id)?.step : undefined}
                    onSave={handleSaveStep}
                    onClose={() => setEditingStep(null)}
                />
            )}
        </div>
    );
};

export default DataPrepPanel;
